import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Loader2, Trash2, RefreshCw, Filter, Phone, Mail, Building2, MessageSquare, ChevronRight, CreditCard, KeyRound } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const fmtEur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

const STATUS_LABEL = {
  new: { label: "Nueva", cls: "bg-sky-100 text-sky-700" },
  contacted: { label: "Contactada", cls: "bg-amber-100 text-amber-700" },
  won: { label: "Ganada", cls: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Perdida", cls: "bg-slate-200 text-slate-600" },
};
const PLAN_LABEL = {
  single_use: "Uso único",
  subscription_monthly: "Suscripción mensual",
  subscription_annual: "Suscripción anual",
  no_idea: "Pide información",
};

const SOURCE_LABELS = {
  web_login: "pantalla de acceso",
  web_info: "web de información",
  web_dossier: "dossier de rentabilidad",
  simulator: "simulador",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function LeadsPage() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, contacted: 0, won: 0, lost: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [chargeFor, setChargeFor] = useState(null); // lead seleccionado para cobrar
  const [packages, setPackages] = useState([]);
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data?.packages || [])).catch(() => {});
  }, []);

  const startCheckout = async (planId) => {
    if (!chargeFor) return;
    setCreating(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
        lead_id: chargeFor.id,
        customer_email: chargeFor.email,
        customer_name: chargeFor.name,
      });
      // Abrir Stripe en una nueva pestaña para preservar el contexto admin
      window.open(data.url, "_blank");
      toast.success("Sesión de pago creada. Se ha abierto en una pestaña nueva.");
      setChargeFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la sesión de pago");
    } finally {
      setCreating(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { status: statusFilter } });
      setLeads(data?.leads || []);
      setCounts({ total: 0, new: 0, contacted: 0, won: 0, lost: 0, ...(data?.counts || {}) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la bandeja de solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Estado actualizado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta solicitud? Esta acción no se puede deshacer.")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Solicitud eliminada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="leads-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Admin · CRM</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Solicitudes de contratación</h1>
          <p className="text-slate-500 mt-1 text-sm">Bandeja de leads recibidos desde el simulador y otros puntos de la app.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm border border-slate-300 hover:bg-slate-50"
          data-testid="leads-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Refrescar
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          { k: "all", label: "Todas", n: counts.total },
          { k: "new", label: "Nuevas", n: counts.new || 0 },
          { k: "contacted", label: "Contactadas", n: counts.contacted || 0 },
          { k: "won", label: "Ganadas", n: counts.won || 0 },
          { k: "lost", label: "Perdidas", n: counts.lost || 0 },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setStatusFilter(s.k)}
            className={`text-left rounded-lg border p-4 transition-all ${
              statusFilter === s.k
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white hover:border-slate-400"
            }`}
            data-testid={`leads-filter-${s.k}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 flex items-center gap-1">
              <Filter className="h-3 w-3" /> {s.label}
            </div>
            <div className="font-display text-3xl font-extrabold mt-1 tabular-nums">{s.n}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2" data-testid="leads-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="leads-empty">
          <Inbox className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display text-xl font-bold mb-1">Sin solicitudes</div>
          <div className="text-slate-500 text-sm">Cuando alguien rellene el formulario "¿CÓMO CONTRATAR?" del simulador aparecerá aquí.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {leads.map((l) => {
            const s = STATUS_LABEL[l.status] || STATUS_LABEL.new;
            const isBusy = busy === l.id;
            return (
              <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`lead-${l.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold">{l.name}</div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                      {l.plan_interest && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          {PLAN_LABEL[l.plan_interest] || l.plan_interest}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {fmtDate(l.created_at)} · desde{" "}
                      <span className="font-semibold">{SOURCE_LABELS[l.source] || l.source || "app"}</span>
                      {l.user_email && <> · usuario <span className="font-mono">{l.user_email}</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {l.license_code && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full" title="Licencia generada">
                        <KeyRound className="h-3 w-3" /> {l.license_code}
                      </span>
                    )}
                    {!l.license_code && (
                      <button
                        onClick={() => setChargeFor(l)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-sky-500 hover:bg-sky-600 text-white px-3 py-1 rounded-full"
                        data-testid={`lead-charge-${l.id}`}
                      >
                        <CreditCard className="h-3 w-3" /> Cobrar
                      </button>
                    )}
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <button
                        key={k}
                        onClick={() => updateStatus(l.id, k)}
                        disabled={isBusy || l.status === k}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md transition-colors ${
                          l.status === k ? "bg-slate-900 text-white cursor-default" : "text-slate-500 hover:bg-slate-100"
                        }`}
                        data-testid={`lead-status-${l.id}-${k}`}
                      >
                        {v.label}
                      </button>
                    ))}
                    <button
                      onClick={() => remove(l.id)}
                      disabled={isBusy}
                      className="ml-2 p-2 rounded-md text-rose-500 hover:bg-rose-50"
                      title="Eliminar"
                      data-testid={`lead-delete-${l.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Farmacia</div>
                      <div>{l.pharmacy || "—"}</div>
                      {l.city && <div className="text-xs text-slate-500">{l.city}</div>}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Teléfono</div>
                      {l.phone ? (
                        <a href={`tel:${l.phone}`} className="text-sky-600 hover:underline">{l.phone}</a>
                      ) : (
                        <div>—</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Email</div>
                      {l.email ? (
                        <a href={`mailto:${l.email}`} className="text-sky-600 hover:underline break-all">{l.email}</a>
                      ) : (
                        <div>—</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Origen usuario</div>
                      <div className="text-xs">{l.user_pharmacy || l.user_email || "—"}</div>
                    </div>
                  </div>
                </div>

                {l.message && (
                  <div className="mt-4 bg-slate-50 rounded-md p-3 text-sm text-slate-700 flex gap-2">
                    <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>{l.message}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!chargeFor} onOpenChange={(o) => { if (!o) setChargeFor(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="charge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-sky-500" /> Cobrar a {chargeFor?.name}
            </DialogTitle>
            <DialogDescription>
              Selecciona el plan a cobrar. Se creará una sesión de Stripe Checkout, y al confirmarse el pago se generará automáticamente una licencia y se vinculará a esta solicitud.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {packages.map((p) => (
              <button
                key={p.id}
                onClick={() => startCheckout(p.id)}
                disabled={!!creating}
                className="text-left bg-white border-2 border-slate-200 hover:border-sky-400 rounded-xl p-4 transition-all disabled:opacity-50"
                data-testid={`charge-plan-${p.id}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{p.label}</div>
                <div className="font-display text-3xl font-extrabold mt-1 tabular-nums">{fmtEur(p.amount)}</div>
                <div className="text-xs text-slate-500 mt-1">{p.description}</div>
                {creating === p.id && (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-sky-600">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Creando sesión…
                  </div>
                )}
              </button>
            ))}
          </div>
          <DialogFooter className="!justify-start">
            <button
              onClick={() => setChargeFor(null)}
              className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100"
              data-testid="charge-cancel"
            >
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
